import { Suspense, useState } from "react";
import type { ContentFileDto } from "shared";
import { Icon } from "../../common/Icon";
import { Spinner } from "../../common/Spinner";
import { useSuspenseMediaItem } from "../../content/useMediaBatch";
import styles from "./MediaItem.module.css";

function getThumbnail(file: ContentFileDto) {
	if (Object.hasOwn(file.thumbnails, "256")) {
		return file.thumbnails["256"];
	}

	return file.url;
}

interface MediaItemProps {
	fileID: number;
}

export function MediaItem(props: MediaItemProps) {
	return (
		<div className={styles.container}>
			<Suspense fallback={<MediaItemLoading />}>
				<MediaItemContent {...props} />
			</Suspense>
		</div>
	);
}

function MediaItemLoading() {
	return (
		<div className={styles.overlay}>
			<Spinner />
		</div>
	);
}

function MediaItemError() {
	return (
		<div className={styles.overlay}>
			<Icon name="error" size={32} />
			There was an error loading media
		</div>
	);
}

function MediaItemContent(props: MediaItemProps) {
	const file = useSuspenseMediaItem(props.fileID);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);

	const handleLoad = () => {
		setLoading(false);
		setError(false);
	};

	const handleError = () => {
		setLoading(false);
		setError(true);
	};

	return (
		<>
			{loading && <MediaItemLoading />}
			{error && <MediaItemError />}
			{file.type === "video" && (
				<div className={styles.video}>
					<Icon name="videocam" size={18} />
				</div>
			)}
			{file.isUsed && <div className={styles.used}>Already used</div>}
			{file.type === "image" && (
				<img
					src={getThumbnail(file)}
					alt={file.filename}
					onLoad={handleLoad}
					onError={handleError}
				/>
			)}
			{file.type === "video" && (
				<video
					tabIndex={-1}
					muted
					onError={handleError}
					// onLoadedData is fired one first frame is loaded
					onLoadedData={handleLoad}
				>
					<source src={file.url} type="video/mp4" />
				</video>
			)}
		</>
	);
}
